/**
 * 音楽再生で使う署名付きURLのキャッシュ
 *
 * 要素再生エンジンとミックス再生エンジンの両方から参照するため、
 * どちらにも属さないモジュールとして切り出している。
 * 署名はパスに対して行われるので、`stem` などのクエリは取得後に付け足してよい。
 */

import { getSignedUrl } from '@/api/api'
import { resolveDirectUrl } from '@/api/client'

import type { StemVariant } from './music-stem-plan'

type CachedUrl = { url: string; expiresAt: number }

/** 署名付きURLの expires が読めなかった場合のフォールバック TTL */
const FALLBACK_TTL_MS = 50 * 60 * 1000

/** クロックずれ・リクエスト中の期限切れを避ける安全マージン */
const SAFETY_MARGIN_MS = 60 * 1000

const urlCache = new Map<string, CachedUrl>()
const inFlight = new Map<string, Promise<string | null>>()

export const streamPath = (episodeId: number): string => `/e/${episodeId}/stream`

/** 署名付きURLに stem クエリを足す（null なら原曲のまま） */
export const withStem = (url: string, stem: StemVariant | null): string => (stem ? `${url}&stem=${stem}` : url)

const parseExpiresAt = (url: string): number => {
  try {
    const expires = new URL(url, window.location.origin).searchParams.get('expires')
    const expiresSec = expires ? Number.parseInt(expires, 10) : Number.NaN
    if (Number.isFinite(expiresSec) && expiresSec > 0) return expiresSec * 1000 - SAFETY_MARGIN_MS
  } catch {
    // URL として解釈できない場合はフォールバックへ
  }
  return Date.now() + FALLBACK_TTL_MS
}

/** キャッシュ済みの署名付きURLを同期的に取り出す（無ければ null） */
export const peekSigned = (path: string): string | null => {
  const cached = urlCache.get(path)
  if (cached && Date.now() < cached.expiresAt) return cached.url
  urlCache.delete(path)
  return null
}

/** 署名付きURLを取得する。同一パスの多重リクエストはまとめる */
export const resolveSigned = (path: string): Promise<string | null> => {
  const cached = peekSigned(path)
  if (cached) return Promise.resolve(cached)

  const pending = inFlight.get(path)
  if (pending) return pending

  const task = getSignedUrl(path)
    .then((signed) => {
      const url = resolveDirectUrl(signed)
      urlCache.set(path, { url, expiresAt: parseExpiresAt(url) })
      return url
    })
    .catch((err: unknown) => {
      console.error('[MusicAudio] 署名付きURLの取得に失敗しました:', err)
      return null
    })
    .finally(() => {
      inFlight.delete(path)
    })

  inFlight.set(path, task)
  return task
}

/** 期限切れの署名を掴んでいる可能性があるときに捨てる */
export const invalidateSigned = (path: string): void => {
  urlCache.delete(path)
}
